import { FullscreenOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Modal, Splitter, Tooltip } from 'antd';
import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import type { IFile } from '../../types/file';
import ComparisonPane from './ComparisonPane';
import {
  extractFilesFromJson,
  parseDocumentData,
  type IPaneState,
} from './utils';

interface IDocumentComparisonProps {
  data: IFile[];
  modelInstanceId?: string;
  height?: string;
  initialPaneCount?: number;
  addPaneText?: string;
  selectFilePlaceholder?: string;
  downloadButtonText?: string;
  previewButtonNotAvailableText?: string;
  emptyStateText?: string;
  shouldShowFullSize?: boolean;
  shouldShowDownload?: boolean;
  jsonData?: Object;
}

let paneCounter = 0;
const newPaneId = () => `pane-${++paneCounter}`;

const makePane = (): IPaneState => ({ id: newPaneId(), selectedFileId: null });

const DocumentComparison: React.FC<IDocumentComparisonProps> = ({
  data,
  modelInstanceId,
  height = '700px',
  initialPaneCount = 1,
  addPaneText = 'Add pane',
  selectFilePlaceholder = 'Select a file',
  downloadButtonText,
  previewButtonNotAvailableText,
  emptyStateText,
  shouldShowFullSize = true,
  shouldShowDownload,
  jsonData,
}) => {
  const [panes, setPanes] = useState<IPaneState[]>(() =>
    Array.from({ length: Math.max(1, initialPaneCount) }, makePane)
  );
  const [isFullscreen, setIsFullscreen] = useState(false);

  const effectiveData = useMemo(() => {
    const fromJson = jsonData ? extractFilesFromJson(jsonData) : [];
    const combined = [...(data ?? []), ...fromJson];
    const seen = new Set<string>();
    return combined.filter(f =>
      seen.has(f.id) ? false : (seen.add(f.id), true)
    );
  }, [data, jsonData]);

  const parsed = useMemo(
    () => parseDocumentData(effectiveData),
    [effectiveData]
  );

  const addPane = () => setPanes(prev => [...prev, makePane()]);

  const removePane = (id: string) =>
    setPanes(prev => prev.filter(p => p.id !== id));

  const selectFile = (paneId: string, fileId: string | null) =>
    setPanes(prev =>
      prev.map(p => (p.id === paneId ? { ...p, selectedFileId: fileId } : p))
    );

  const renderPanes = (splitterHeight: string) => (
    <Splitter style={{ height: splitterHeight }}>
      {panes.map(pane => (
        <Splitter.Panel key={pane.id} min="220px">
          <ComparisonPane
            pane={pane}
            treeSelectData={parsed.treeSelectData}
            nodeMap={parsed.nodeMap}
            canRemove={panes.length > 1}
            onSelectFile={fileId => selectFile(pane.id, fileId)}
            onRemove={() => removePane(pane.id)}
            modelInstanceId={modelInstanceId}
            selectFilePlaceholder={selectFilePlaceholder}
            downloadButtonText={downloadButtonText}
            previewButtonNotAvailableText={previewButtonNotAvailableText}
            emptyStateText={emptyStateText}
            shouldShowDownload={shouldShowDownload}
          />
        </Splitter.Panel>
      ))}
    </Splitter>
  );

  const renderToolbarActions = () => (
    <>
      {shouldShowFullSize && (
        <Tooltip title="Fullscreen">
          <Button
            size="small"
            type="text"
            icon={<FullscreenOutlined />}
            onClick={() => setIsFullscreen(true)}
          />
        </Tooltip>
      )}
      <Button size="small" icon={<PlusOutlined />} onClick={addPane}>
        {addPaneText}
      </Button>
    </>
  );

  return (
    <>
      <Wrapper>
        <Toolbar>{renderToolbarActions()}</Toolbar>
        {renderPanes(height)}
      </Wrapper>
      <Modal
        open={isFullscreen}
        onCancel={() => setIsFullscreen(false)}
        title={null}
        footer={null}
        width="100vw"
        style={{ maxWidth: '100vw', top: 0, paddingBottom: 0 }}
        styles={{
          content: {
            height: '100dvh',
            borderRadius: 0,
            display: 'flex',
            flexDirection: 'column',
          },
          body: {
            flex: 1,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            padding: 0,
          },
        }}
      >
        <ModalToolbar>{renderToolbarActions()}</ModalToolbar>
        {renderPanes('100%')}
      </Modal>
    </>
  );
};

export default DocumentComparison;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  overflow: hidden;
  background: ${({ theme }) => theme.colors.surface};
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing.xs};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  flex-shrink: 0;
`;

const ModalToolbar = styled(Toolbar)`
  border-radius: 0;
  border-top: none;
  border-left: none;
  border-right: none;
`;
